import { useEffect, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';

import { useAppAccess } from '@/hooks/useAppAccess';

interface RequireAppAccessProps {
  /** App identifier, e.g. `ASSET_LIBRARY`. */
  app: string;
  children: ReactNode;
}

/**
 * Renders children only for users granted the given suite app.
 *
 * The app's entry points are already hidden from users without the grant; this
 * covers the remaining ways in — a bookmark, a shared link, the back button —
 * by sending them to the hub rather than to a page that would only fill with
 * 403s.
 *
 * This is not a security boundary. The Asset Library API enforces the same
 * grant itself, because the browser calls that service directly and nothing
 * here is in that path.
 */
export function RequireAppAccess({ app, children }: RequireAppAccessProps) {
  const { allowed, isLoading, isError, refetch } = useAppAccess(app);
  const navigate = useNavigate();

  useEffect(() => {
    // Only redirect on a definite "no". While the grant is loading, or when the
    // lookup failed outright, it is unknown rather than denied — bouncing the
    // user then would read as a silent, unexplained revocation.
    if (!isLoading && !isError && !allowed) {
      // `replace` so the back button doesn't bounce between here and the hub.
      navigate({ to: '/suite', replace: true });
    }
  }, [allowed, isLoading, isError, navigate]);

  if (isError) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-red-200 bg-white">
        <div className="max-w-md text-center">
          <h2 className="text-[15.5px] font-bold text-slate-800">Couldn’t check your access</h2>
          <p className="mt-2 text-[12.5px] leading-relaxed text-slate-500">
            We couldn’t confirm which apps you’ve been granted. This is a loading problem, not a
            sign that your access was removed.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 rounded-md bg-slate-800 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-slate-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // A spinner rather than nothing: this component's whole job is cold entry
  // (bookmark, refresh, shared link), where a blank pane reads as a failure.
  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  // Render nothing once denied: showing the page first and pulling it away
  // would flash content the user is not entitled to.
  if (!allowed) return null;

  return <>{children}</>;
}
